/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { usePathname } from 'next/navigation'

// import components
import Icon from '@mdi/react'
import Link from 'next/link'
import { Button as ButtonBase } from '@headlessui/react'

export type SharedButtonProps = {
	onClick?: () => void
	children?: React.ReactNode
	disabled?: boolean
	active?: boolean
	type?: 'button' | 'submit' | 'reset'
}

export const Button = ({ onClick, children, disabled, type }: SharedButtonProps) => (
	<ButtonBase type={type} className='h-10 w-40 shrink-0 rounded bg-700 px-6 font-semibold enabled:hover:bg-600 disabled:cursor-not-allowed' onClick={onClick} disabled={disabled}>{children}</ButtonBase>
)

export const FlexButton = ({ onClick, children, disabled, type }: SharedButtonProps) => (
	<ButtonBase type={type} className='h-10 shrink-0 rounded bg-700 px-6 font-semibold enabled:hover:bg-600 disabled:cursor-not-allowed' onClick={onClick} disabled={disabled}>{children}</ButtonBase>
)

export const FullButton = ({ onClick, children, disabled, type }: SharedButtonProps) => (
	<ButtonBase type={type} className='h-10 shrink-0 grow rounded border border-200 bg-white font-semibold enabled:hover:bg-100 disabled:cursor-not-allowed disabled:text-400' onClick={onClick} disabled={disabled}>{children}</ButtonBase>
)

export const DeleteButton = ({ onClick, children, disabled, type }: SharedButtonProps) => (
	<ButtonBase type={type} className='h-10 shrink-0 rounded border border-red-700 bg-red-500 px-6 font-semibold text-white enabled:hover:bg-red-600 disabled:cursor-not-allowed' onClick={onClick} disabled={disabled}>{children}</ButtonBase>
)

export const IconButton = ({ active, icon, onClick, disabled }: SharedButtonProps & { icon: string }) => (
	<ButtonBase className={'flex size-10 items-center justify-center rounded hover:bg-600 ' + (active ? '' : 'text-400')} onClick={onClick} disabled={disabled}>
		<Icon path={icon} size={1} />
	</ButtonBase>
)

export const PopoverIconButton = ({ icon, onClick, disabled }: SharedButtonProps & { icon: string }) => (
	<ButtonBase className={'flex size-10 shrink-0 items-center justify-center rounded border border-200 bg-white enabled:hover:bg-100 disabled:cursor-not-allowed disabled:text-400'} onClick={onClick} disabled={disabled} >
		<Icon path={icon} size={1} />
	</ButtonBase >
)

export const WideIconButton = ({ onClick, children, disabled, type, icon, full }: SharedButtonProps & { icon: string, full?: boolean }) => (
	<ButtonBase type={type} onClick={onClick} className={`flex shrink-0 items-center gap-2 rounded bg-700 p-2 enabled:hover:bg-600 disabled:cursor-not-allowed disabled:text-400 ${full ? 'w-full' : 'w-10 max-w-48 lg:w-full'}`} disabled={disabled}>
		<Icon path={icon} size={1} />
		{children}
	</ButtonBase>
)

export const TabLink = ({ href, label }: { href: string, label: string }) => {
	const pathname = usePathname()

	return (
		<Link href={href} className='flex h-10 w-32 items-center justify-center rounded px-3 hover:bg-100'>
			<div className={pathname === href ? 'font-semibold' : 'font-light'}>{label}</div>
		</Link>
	)
}
